import { Permission, type PermissionValue } from '../auth/types';

/**
 * Workspace ID types (UI views, not permission roles)
 */
export type WorkspaceId = 
    | 'stage-management'
    | 'costumes'
    | 'props'
    | 'sound'
    | 'lighting'
    | 'director'
    | 'production-manager'
    | 'cast-member'
    | 'performance'
    | 'all-tools';

/**
 * Tool ID types (maps to existing tab IDs)
 */
export type ToolId = 
    | 'dashboard'
    | 'show-structure'
    | 'rehearsal-script'
    | 'personnel'
    | 'scheduler'
    | 'calendar' // New display widget
    | 'notes'
    | 'action-items'
    | 'set-builder'
    | 'blocking'
    | 'blocking-snapshot' // New read-only widget
    | 'props'
    | 'costumes'
    | 'sound'
    | 'video' // Video Chat
    | 'email' // Communications
    | 'feed'
    | 'files'
    | 'scenes'
    | 'budget'
    | 'production'
    | 'settings'
    | 'lighting'
    | 'prompt-book'
    | 'line-notes'
    | 'performance'
    | 'projections'
    | 'activity-log'
    | 'dev';

/**
 * Panel configuration — a single tool in a multi-panel layout
 */
export interface PanelConfig {
    toolId: ToolId;
    size: number;           // flex ratio (e.g., 60 = 60% of parent)
    minSize?: number;       // minimum % before collapse
}

/**
 * Layout configuration — recursive split tree
 * Supports nested horizontal/vertical splits (like Premiere Pro)
 */
export interface LayoutConfig {
    direction: 'horizontal' | 'vertical';
    panels: (PanelConfig | LayoutConfig)[];  // recursive for nested splits
}

/**
 * Workspace configuration
 */
export interface WorkspaceConfig {
    id: WorkspaceId;
    label: string;
    icon: string; // Material Symbols
    description: string;
    primaryTools: ToolId[];
    requiredPermissions?: PermissionValue[]; // If user lacks these, workspace is hidden
}

/**
 * Tool configuration
 */
export interface ToolConfig {
    id: ToolId;
    label: string;
    icon: string;
    route: string; // URL path
    routeAliases?: string[];
    requiredPermissions?: PermissionValue[]; // If user lacks these, tool is hidden
    /** Minimum viewport width in px. If set, tool shows a DeviceGateBanner below this width. */
    minWidth?: number;
    /** If true, tool requires scriptTextAccess on the project to be visible. */
    requiresScriptText?: boolean;
}

interface ScriptToolAccess {
    canUseScriptEditor?: boolean;
}

/**
 * Workspace definitions
 */
export const WORKSPACES: WorkspaceConfig[] = [
    {
        id: 'stage-management',
        label: 'Rehearsal Room',
        icon: 'theater_comedy',
        description: 'Core SM workflow — scripts, cues, and calling',
        primaryTools: ['dashboard', 'scenes', 'prompt-book', 'blocking', 'notes', 'action-items', 'line-notes', 'scheduler', 'personnel', 'show-structure', 'video', 'email', 'activity-log'],
        requiredPermissions: [Permission.EDIT_RUNSHEET],
    },
    {
        id: 'costumes',
        label: 'Wardrobe',
        icon: 'checkroom',
        description: 'Costume design and fittings',
        primaryTools: ['dashboard', 'costumes', 'personnel', 'budget', 'files'],
        requiredPermissions: [Permission.EDIT_COSTUMES],
    },
    {
        id: 'props',
        label: 'Workshop',
        icon: 'inventory_2',
        description: 'Props management and budgets',
        primaryTools: ['dashboard', 'props', 'budget', 'files', 'scenes'],
        requiredPermissions: [Permission.EDIT_PROPS],
    },
    {
        id: 'sound',
        label: 'Sound Booth',
        icon: 'graphic_eq',
        description: 'Sound design, cues, and mic packs',
        primaryTools: ['dashboard', 'sound', 'scenes', 'files', 'scheduler'],
        requiredPermissions: [Permission.EDIT_SOUND],
    },
    {
        id: 'lighting',
        label: 'Light Lab',
        icon: 'tungsten',
        description: 'Lighting design, cues, and follow spots',
        primaryTools: ['dashboard', 'lighting', 'scenes', 'files', 'scheduler'],
        requiredPermissions: [Permission.EDIT_LIGHTING],
    },
    {
        id: 'director',
        label: 'Creative Suite',
        icon: 'movie_creation',
        description: 'Creative oversight and vision',
        primaryTools: ['dashboard', 'show-structure', 'blocking', 'scheduler', 'notes', 'video', 'activity-log'],
        requiredPermissions: [Permission.EDIT_SCHEDULER],
    },
    {
        id: 'production-manager',
        label: 'Production Office',
        icon: 'assignment',
        description: 'Administrative oversight and budgets',
        primaryTools: ['dashboard', 'production', 'action-items', 'budget', 'personnel', 'scheduler', 'files', 'video', 'email', 'activity-log'],
        requiredPermissions: [Permission.MANAGE_MEMBERS],
    },
    {
        id: 'cast-member',
        label: 'Green Room',
        icon: 'groups',
        description: 'Actor-focused schedule and blocking',
        primaryTools: ['dashboard', 'calendar', 'rehearsal-script', 'blocking-snapshot', 'show-structure', 'personnel', 'files'],
        requiredPermissions: [Permission.VIEW_PROJECT],
    },
    {
        id: 'performance',
        label: 'Show Booth',
        icon: 'podium',
        description: 'Live show calling and cue execution',
        primaryTools: ['performance'],
        requiredPermissions: [Permission.CALL_SHOW],
    },
    {
        id: 'all-tools',
        label: 'Backstage',
        icon: 'apps',
        description: 'Access all available tools',
        primaryTools: [], // Dynamically populated based on permissions
    },
];

/**
 * All available tools
 */
export const TOOLS: ToolConfig[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home', route: '/dashboard' },
    { id: 'show-structure', label: 'Show Structure', icon: 'list_alt', route: '/show-structure' },
    { id: 'rehearsal-script', label: 'Rehearsal Script', icon: 'auto_stories', route: '/rehearsal-script', requiredPermissions: [Permission.VIEW_PROJECT], requiresScriptText: true },
    { id: 'personnel', label: 'Personnel', icon: 'people', route: '/personnel', routeAliases: ['/cast'], requiredPermissions: [Permission.VIEW_PROJECT] },
    { id: 'scheduler', label: 'Scheduler', icon: 'calendar_month', route: '/scheduler', requiredPermissions: [Permission.EDIT_SCHEDULER] },
    { id: 'calendar', label: 'Calendar', icon: 'calendar_today', route: '/calendar', requiredPermissions: [Permission.VIEW_PROJECT] },
    { id: 'notes', label: 'Notes', icon: 'note', route: '/notes', requiredPermissions: [Permission.EDIT_NOTES] },
    { id: 'action-items', label: 'Action Items', icon: 'task_alt', route: '/action-items', requiredPermissions: [Permission.VIEW_ACTION_ITEMS] },
    { id: 'set-builder', label: 'Set Builder', icon: 'view_in_ar', route: '/set-builder', requiredPermissions: [Permission.EDIT_SETS] },
    { id: 'blocking', label: 'Blocking', icon: 'directions_walk', route: '/blocking', requiredPermissions: [Permission.EDIT_BLOCKING] },
    { id: 'blocking-snapshot', label: 'Blocking View', icon: 'remove_red_eye', route: '/blocking-view', requiredPermissions: [Permission.VIEW_PROJECT], minWidth: 768 },
    { id: 'props', label: 'Props', icon: 'inventory_2', route: '/props', requiredPermissions: [Permission.EDIT_PROPS] },
    { id: 'costumes', label: 'Costumes', icon: 'checkroom', route: '/costumes', requiredPermissions: [Permission.EDIT_COSTUMES] },
    { id: 'sound', label: 'Sound', icon: 'graphic_eq', route: '/sound', requiredPermissions: [Permission.EDIT_SOUND] },
    { id: 'lighting', label: 'Lighting', icon: 'tungsten', route: '/lighting', requiredPermissions: [Permission.EDIT_LIGHTING], minWidth: 768 },
    { id: 'prompt-book', label: 'Prompt Book', icon: 'menu_book', route: '/prompt-book', requiredPermissions: [Permission.VIEW_PROMPTBOOK], minWidth: 768, requiresScriptText: true },
    { id: 'video', label: 'Video', icon: 'videocam', route: '/video', requiredPermissions: [Permission.VIEW_PROJECT] },
    { id: 'email', label: 'Communications', icon: 'mail', route: '/email', requiredPermissions: [Permission.SEND_EMAIL, Permission.MANAGE_COMMUNICATIONS] },
    { id: 'feed', label: 'Feed', icon: 'campaign', route: '/feed', requiredPermissions: [Permission.VIEW_PROJECT] },
    { id: 'files', label: 'Files', icon: 'folder', route: '/files', requiredPermissions: [Permission.VIEW_PROJECT] },
    { id: 'scenes', label: 'Run Sheet', icon: 'view_list', route: '/scenes', requiredPermissions: [Permission.EDIT_RUNSHEET], minWidth: 768 },
    { id: 'budget', label: 'Budget', icon: 'attach_money', route: '/budget', requiredPermissions: [Permission.VIEW_BUDGET], minWidth: 768 },
    { id: 'production', label: 'Production', icon: 'dashboard', route: '/production', requiredPermissions: [Permission.VIEW_PRODUCTION], minWidth: 768 },
    { id: 'settings', label: 'Settings', icon: 'settings', route: '/settings' },
    { id: 'line-notes', label: 'Line Notes', icon: 'edit_note', route: '/line-notes', requiredPermissions: [Permission.EDIT_RUNSHEET], requiresScriptText: true },
    { id: 'activity-log', label: 'Activity Log', icon: 'schedule', route: '/activity-log', requiredPermissions: [Permission.VIEW_ALL] },
    { id: 'performance', label: 'Performance', icon: 'podium', route: '/performance', requiredPermissions: [Permission.CALL_SHOW], requiresScriptText: true },
    ...(import.meta.env.DEV ? [{ id: 'dev' as ToolId, label: 'Kitchen Sink', icon: 'science', route: '/dev' }] : []),
];

/**
 * Get workspace configuration by ID
 */
export function getWorkspace(id: WorkspaceId): WorkspaceConfig | undefined {
    return WORKSPACES.find(w => w.id === id);
}

/**
 * Get tool configuration by ID
 */
export function getTool(id: ToolId): ToolConfig | undefined {
    return TOOLS.find(t => t.id === id);
}

const TOOL_IDS = new Set<string>(TOOLS.map(tool => tool.id));

export function isToolId(value: string): value is ToolId {
    return TOOL_IDS.has(value);
}

export function getRouteForTool(id: ToolId): string {
    return getTool(id)?.route ?? '/';
}

export function getToolByRoute(pathname: string): ToolConfig | undefined {
    return TOOLS.find(tool => (
        tool.route === pathname || tool.routeAliases?.includes(pathname)
    ));
}

/**
 * Get all tools the user has permission to access
 */
export function getAccessibleTools(userPermissions: PermissionValue[]): ToolConfig[] {
    return TOOLS.filter(tool => {
        if (!tool.requiredPermissions) return true;
        return tool.requiredPermissions.some(p => userPermissions.includes(p));
    });
}

/**
 * Get all workspaces the user has permission to access
 */
export function getAccessibleWorkspaces(userPermissions: PermissionValue[]): WorkspaceConfig[] {
    return WORKSPACES.filter(workspace => {
        if (!workspace.requiredPermissions) return true;
        return workspace.requiredPermissions.some(p => userPermissions.includes(p));
    });
}

/**
 * Filter tools based on script text access.
 * When scriptTextAccess is false, tools with requiresScriptText are removed.
 */
export function filterScriptGatedTools(
    tools: ToolConfig[],
    scriptTextAccess: boolean,
    _scriptToolAccess: ScriptToolAccess = {},
): ToolConfig[] {
    const filteredTools = tools.filter(tool => {
        if (tool.requiresScriptText && !scriptTextAccess) {
            return false;
        }

        return true;
    });

    return filteredTools.length === tools.length ? tools : filteredTools;
}

export function hasResolvedScriptTextToolAccess({
    cloudEnabled,
    scriptTextAccess,
}: {
    cloudEnabled: boolean;
    scriptTextAccess?: boolean;
}): boolean {
    return !cloudEnabled || typeof scriptTextAccess === 'boolean';
}

export function getRenderableToolId({
    activeToolId,
    cloudEnabled,
    fallbackToolId,
    scriptTextAccess,
}: {
    activeToolId: ToolId;
    cloudEnabled: boolean;
    fallbackToolId: ToolId;
    scriptTextAccess?: boolean;
}): ToolId {
    const activeTool = getTool(activeToolId);
    if (!cloudEnabled || activeTool?.requiresScriptText !== true) {
        return activeToolId;
    }

    return scriptTextAccess === true ? activeToolId : fallbackToolId;
}

export function getRenderableOptionalToolId({
    activeToolId,
    cloudEnabled,
    fallbackToolId,
    scriptTextAccess,
}: {
    activeToolId: ToolId | null;
    cloudEnabled: boolean;
    fallbackToolId: ToolId;
    scriptTextAccess?: boolean;
}): ToolId | null {
    return activeToolId
        ? getRenderableToolId({ activeToolId, cloudEnabled, fallbackToolId, scriptTextAccess })
        : null;
}

/**
 * Determine the best default workspace for a user based on their permissions
 */
export function getDefaultWorkspace(userPermissions: PermissionValue[]): WorkspaceId {
    if (userPermissions.includes(Permission.EDIT_RUNSHEET)) {
        return 'stage-management';
    }
    if (userPermissions.includes(Permission.MANAGE_MEMBERS)) {
        return 'production-manager';
    }
    if (userPermissions.includes(Permission.EDIT_SCHEDULER)) {
        return 'director';
    }
    if (userPermissions.includes(Permission.EDIT_PROPS)) {
        return 'props';
    }
    if (userPermissions.includes(Permission.EDIT_SOUND)) {
        return 'sound';
    }
    if (userPermissions.includes(Permission.VIEW_PROJECT)) {
        return 'cast-member';
    }
    
    // Default to all-tools if can't determine
    return 'all-tools';
}





